import React, { useState } from 'react';
import AdminLayout from '../../../layouts/AdminLayout';
import { Card, Col, Row } from 'react-bootstrap';
import { toast } from 'sonner';
import { IconBuilding, IconCalendar, IconCopy, IconCurrencyDollar, IconGlobe, IconKey, IconMail, IconMapPin, IconPhone, IconUser } from '@tabler/icons-react';
import { useGetMyPropertyQuery } from '../hooks/useGetMyPropertyQuery';
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner';
import FullPageError from '../../../shared/components/errors/FullPageError';
import UpdatePropertyDialog from '../components/UpdatePropertyDialog';
import BankAccountCard from '../components/BankAccountCard';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { useAuth } from '../../../auth/context/AuthProvider';

// =================================================================
// Perbaikan pada Komponen `DataRow`
// =================================================================
interface DataRowProps {
    icon: React.ElementType;
    label: string;
    value: string | number | JSX.Element | null | undefined;
}

const DataRow: React.FC<DataRowProps> = ({ icon: Icon, label, value }) => (
    // Tambahkan mb-3 untuk spasi vertikal antar baris
    <Row className="mb-3">
        {/* Di mobile (xs), label akan full width. Di desktop (md), hanya 4 kolom. */}
        <Col xs={12} md={4} className="d-flex align-items-center fw-bold text-muted">
            <Icon size={18} className="me-2 flex-shrink-0" />
            <span>{label}</span>
        </Col>
        {/* Di mobile, value akan berada di bawah label. Di desktop, di sampingnya. */}
        <Col xs={12} md={8}>
            {value || "N/A"}
        </Col>
    </Row>
);


/**
 * Property ID (UUID) — dibutuhkan saat menghubungkan sistem eksternal,
 * mis. mengisi "PMS Property ID" di POS agar transfer bill ke kamar bekerja.
 * Sebelumnya nilai ini hanya bisa didapat lewat API/database.
 */
const PropertyIdRow: React.FC<{ propertyId: string }> = ({ propertyId }) => {
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(propertyId);
            toast.success('Property ID copied');
        } catch {
            toast.error('Failed to copy. Please select and copy manually.');
        }
    };

    return (
        <Row className="mb-3">
            <Col xs={12} md={4} className="d-flex align-items-center fw-bold text-muted">
                <IconKey size={18} className="me-2 flex-shrink-0" />
                <span>Property ID</span>
            </Col>
            <Col xs={12} md={8} className="d-flex align-items-center gap-2">
                <code className="text-break small">{propertyId}</code>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                    onClick={handleCopy}
                    title="Copy Property ID"
                >
                    <IconCopy size={14} />
                </button>
            </Col>
        </Row>
    );
};

// =================================================================
// Perbaikan pada Komponen `MyPropertyPage`
// =================================================================
const MyPropertyPage: React.FC = () => {
    const [show, setShow] = useState(false);
    const { data: property, isSuccess, isError, error, isLoading } = useGetMyPropertyQuery();
    const { hasRole, hasPermission } = useAuth();
    const canManageBankAccount = hasRole('dev') || hasPermission('update_property');

    if (isError) {
        toast.error(error?.message || "Failed to load property data", {
            id: 'property-error',
        });
    }

    return (
        <AdminLayout>
            <div className="container-fluid my-4 px-4">
                <div className='d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center gap-3 mb-4'>
                    {/* Menambahkan flex-md-grow-1 agar elemen ini mengisi sisa ruang di desktop */}
                    <h5 className="card-title fs-2 fw-bold mb-0 flex-md-grow-1">
                        Property Information
                    </h5>

                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['update_property']} asComponent>
                        {/* Kelas w-md-auto sudah membuat tombol ini "kecil" (sesuai konten) di desktop */}
                        <button className="btn btn-primary w-md-auto" onClick={() => setShow(true)}>
                            Edit My Property
                        </button>
                    </RequireAuth>
                </div>

                {isError && <FullPageError />}
                {isLoading && <FullPageSpinner />}
                {isSuccess && (
                    // Layout dipecah menjadi beberapa kolom untuk kartu
                    <Row>
                        {/* Kartu 1: General Information */}
                        <Col lg={6} xl={4} className="mb-4">
                            <Card className="shadow-sm h-100">
                                <Card.Header className="bg-primary text-white d-flex align-items-center justify-content-between">
                                    <span>General Information</span>
                                    {property.data.logo_url && (
                                        <img src={property.data.logo_url} alt="Property Logo" className="rounded" style={{ height: '50px', objectFit: 'contain' }} />
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    <DataRow icon={IconBuilding} label="Name" value={property.data.name} />
                                    <PropertyIdRow propertyId={property.data.id} />
                                    <DataRow icon={IconBuilding} label="Accommodation Type" value={property.data.accommodation_type} />
                                    <DataRow icon={IconCurrencyDollar} label="Preferred Currency" value={property.data.preferred_currency} />
                                    <DataRow icon={IconCalendar} label="Time Zone" value={property.data.time_zone} />
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Kartu 2: Contact Details */}
                        <Col lg={6} xl={4} className="mb-4">
                            <Card className="shadow-sm h-100">
                                <Card.Header className="bg-primary text-white">Contact Details</Card.Header>
                                <Card.Body>
                                    <DataRow icon={IconMail} label="Primary Email" value={property.data.primary_email} />
                                    <DataRow icon={IconMail} label="CC Emails" value={property.data.cc_emails?.join(", ") || "N/A"} />
                                    <DataRow icon={IconMail} label="Accounting Email" value={property.data.accounting_email} />
                                    <DataRow icon={IconPhone} label="Mobile Number" value={`${property.data.mobile_number_country_code} ${property.data.mobile_number}`} />
                                    <DataRow icon={IconPhone} label="Primary Phone" value={`${property.data.primary_phone_country_code} ${property.data.primary_phone}`} />
                                    <DataRow icon={IconGlobe} label="Website" value={<a href={property.data.website || undefined} target="_blank" rel="noopener noreferrer">{property.data.website || "N/A"}</a>} />
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Kartu 3: Location & Tax */}
                        <Col lg={12} xl={4} className="mb-4">
                            <Card className="shadow-sm h-100">
                                <Card.Header className="bg-primary text-white">Location & Tax</Card.Header>
                                <Card.Body>
                                    <DataRow icon={IconMapPin} label="Country" value={property.data.country} />
                                    <DataRow icon={IconMapPin} label="State" value={property.data.state} />
                                    <DataRow icon={IconMapPin} label="City" value={property.data.city} />
                                    <DataRow icon={IconMapPin} label="Street Address" value={property.data.street_address} />
                                    <DataRow icon={IconMapPin} label="Postal Code" value={property.data.postal_code} />
                                    <DataRow icon={IconMapPin} label="Latitude / Longitude" value={`${property.data.latitude}, ${property.data.longitude}`} />
                                    <DataRow icon={IconUser} label="Prefix Booking Number" value={property.data.prefix_booking_number} />
                                    <DataRow icon={IconUser} label="Hotel Tax Number" value={property.data.hotel_tax_number} />
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Kartu 4: Bank Account */}
                        <Col lg={6} xl={4} className="mb-4">
                            <BankAccountCard propertyId={property.data.id} canManage={canManageBankAccount} />
                        </Col>
                    </Row>
                )}
            </div>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['update_property']} asComponent>
                <UpdatePropertyDialog show={show} onClose={() => setShow(false)} />
            </RequireAuth>
        </AdminLayout>
    );
};

export default MyPropertyPage;